import { toast } from "sonner";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  CheckCircle2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import PageContainer from "@/components/PageContainer";
import SoftButton from "@/components/SoftButton";
import {
  STREAK_SUGGESTIONS,
  addStreak,
  deleteStreak,
  getLastSevenDays,
  getStreaks,
  isCompleteToday,
  toggleStreakDay,
} from "@/lib/streaks";

export default function Streaks() {
  const [items, setItems] = useState(() => getStreaks());
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");

  const completedToday = useMemo(
    () => items.filter((item) => isCompleteToday(item)).length,
    [items]
  );

  const create = (value = label) => {
    try {
    const clean = value.trim();
    if (!clean) return;
    const item = addStreak({ label: clean });
    setItems((current) => [item, ...current]);
    setLabel("");
    setAdding(false);
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  const toggleToday = (id) => {
    try {
    const updated = toggleStreakDay(id);
    setItems((current) =>
      current.map((item) => (item.id === id ? updated : item))
    );
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  const remove = (id) => {
    try {
    setItems(deleteStreak(id));
    } catch { toast.error("Could not save on this device. Your changes have not been stored."); }
  };

  return (
    <PageContainer className="pt-7" testid="streaks-page">
      <BrandHeader compact />

      <section className="mb-6">
        <p
          className="text-[10px] uppercase tracking-[0.34em] mb-2"
          style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
        >
          Gentle rhythms
        </p>
        <h1
          className="text-[42px] leading-[1.02] tracking-[-0.03em]"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          Notice what you
          <br />
          <em
            className="font-normal"
            style={{ color: "var(--un-burgundy)", fontFamily: "Cormorant Garamond, serif" }}
          >
            return to.
          </em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>
          Notice actions and choices you may want to return to. A missed day is simply a missed day. Return whenever you want.
        </p>
      </section>

      <section
        className="rounded-[1.75rem] p-5 mb-5"
        style={{
          background: "linear-gradient(135deg, rgba(74,45,72,0.98), rgba(109,72,102,0.96))",
          color: "#fffaf5",
          boxShadow: "0 14px 34px rgba(74,45,72,0.13)",
        }}
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] opacity-65">Today</p>
            <p className="mt-1 text-[23px]" style={{ fontFamily: "Lora, serif" }}>
              {completedToday === 0
                ? "Nothing marked today yet."
                : completedToday === 1
                  ? "One thing marked today."
                  : `${completedToday} things marked today.`}
            </p>
          </div>
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.10)" }}
          >
            <CheckCircle2 size={21} strokeWidth={1.7} />
          </div>
        </div>
      </section>

      {items.length === 0 && !adding && (
        <section className="un-card p-5 mb-5">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="mt-0.5" style={{ color: "var(--un-ember)" }} />
            <div>
              <p className="text-[17px] font-medium" style={{ color: "var(--un-text-1)" }}>
                Start with something kind
              </p>
              <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
                Choose something you want more of — calm, movement, sleep, connection, creativity, or your own safer choice.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {STREAK_SUGGESTIONS.slice(0, 6).map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => create(suggestion)}
                className="rounded-full px-3 py-2 text-[12px] un-press"
                style={{ background: "rgba(91,57,84,0.055)", color: "var(--un-plum)" }}
              >
                + {suggestion}
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="space-y-3 mb-5">
        {items.map((item, index) => {
          const done = isCompleteToday(item);
          
          
          const week = getLastSevenDays(item);
          return (
            <motion.section
              key={item.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.03 }}
              className="un-card p-5"
            >
              <div className="flex items-start gap-3">
                <button
                  onClick={() => toggleToday(item.id)}
                  aria-label={done ? `Unmark ${item.label} for today` : `Mark ${item.label} complete for today`}
                  className="w-11 h-11 shrink-0 rounded-2xl flex items-center justify-center un-press"
                  style={{
                    background: done ? "var(--un-plum)" : "rgba(91,57,84,0.055)",
                    color: done ? "#fffaf5" : "var(--un-plum)",
                  }}
                >
                  <Check size={19} strokeWidth={done ? 2.4 : 1.7} />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[17px] font-medium truncate" style={{ color: "var(--un-text-1)" }}>
                    {item.label}
                  </p>
                  <p className="text-[12px] mt-0.5" style={{ color: "var(--un-text-3)" }}>
                    {done ? "Marked for today." : "Open for today — no pressure."}
                  </p>
                </div>
                <button
                  onClick={() => remove(item.id)}
                  aria-label={`Delete ${item.label}`}
                  className="w-9 h-9 rounded-full flex items-center justify-center un-press"
                  style={{ color: "var(--un-text-3)" }}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="flex items-center justify-between mt-4 px-1" aria-label="Last seven days">
                {week.map((day) => (
                  <div key={day.key} className="flex flex-col items-center gap-1.5">
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[10px]"
                      style={{
                        background: day.complete
                          ? "var(--un-plum)"
                          : day.isToday
                            ? "rgba(196,161,106,0.16)"
                            : "rgba(91,57,84,0.045)",
                        color: day.complete ? "#fffaf5" : "var(--un-text-3)",
                        boxShadow: day.isToday ? "0 0 0 1px rgba(196,161,106,0.38)" : "none",
                      }}
                    >
                      {day.complete ? <Check size={12} /> : "·"}
                    </span>
                    <span className="text-[9px]" style={{ color: "var(--un-text-3)" }}>
                      {day.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.section>
          );
        })}
      </div>

      {adding ? (
        <section className="un-card p-5 mb-8">
          <label className="block text-[12px] mb-2" style={{ color: "var(--un-text-2)" }}>
            What would you like more of in your life?
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") create();
            }}
            autoFocus
            placeholder="e.g. 10 minutes outside"
            className="w-full rounded-2xl px-4 py-3 outline-none text-[14px]"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
          <div className="flex gap-2 mt-3">
            <SoftButton variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </SoftButton>
            <SoftButton variant="primary" size="sm" onClick={() => create()} disabled={!label.trim()}>
              Add
            </SoftButton>
          </div>
        </section>
      ) : (
        <SoftButton
          variant="secondary"
          size="lg"
          iconLeft={<Plus size={17} />}
          onClick={() => setAdding(true)}
          className="w-full mb-4"
        >
          Add something positive
        </SoftButton>
      )}

      <section className="rounded-3xl px-5 py-4 mb-8" style={{ background: "rgba(91,57,84,0.035)" }}>
        <p className="text-[11px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
          No badges. No rankings. No punishment for starting again. This is a record of returning to what matters.
        </p>
      </section>
    </PageContainer>
  );
}

function RhythmStat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl px-3 py-3" style={{ background: "rgba(91,57,84,0.04)" }}>
      <div className="flex items-center gap-2">
        <Icon size={13} style={{ color: "var(--un-ember)" }} strokeWidth={1.8} />
        <span
          className="text-[20px] leading-none"
          style={{ color: "var(--un-plum-deep)", fontFamily: "Lora, serif" }}
        >
          {value}
        </span>
      </div>
      <p className="text-[10px] mt-1" style={{ color: "var(--un-text-3)" }}>{label}</p>
    </div>
  );
}
